import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import { StyleSheet, TouchableHighlight, View } from "react-native";
import { RouteProps } from "react-router-dom";

import { parsePhoneNumber } from "libphonenumber-js/mobile";

import {
  Button,
  Link,
  ThemeColors,
  ThemeShadows,
  Typography,
  VStack,
} from "@smartrent/ui";

import FullScreenLoader from "@/components/common/FullScreenLoader";
import Header from "@/components/common/Header";
import { useAppState } from "@/context/AppState";

type Props = {
  header: {
    title?: null | ReactNode;
    left?: null | string;
    right?: null | ReactNode;
  };
  route: null | RouteProps;
  handleReset?: () => void;
  shadows: ThemeShadows;
  colors: ThemeColors;
  showNewTourButton: boolean;
  propertyName: string;
  phoneNumber: null | string;
  children: ReactNode;
};

export default function AppShellBase({
  header = {
    title: null,
    left: "Back",
    right: null,
  },
  route,
  handleReset = () => {},
  colors,
  shadows,
  showNewTourButton = true,
  propertyName = "",
  phoneNumber,
  children,
}: Props) {
  const [state, _setState] = useAppState();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const formattedPhoneNumber = useMemo(() => {
    return phoneNumber
      ? parsePhoneNumber(phoneNumber, "US").formatNational()
      : "";
  }, [phoneNumber]);

  if (!state.config) {
    // This is needed to prevent flicker of Theme Color
    // Note: this flicker color bug was on main, not introduced by this commit
    return <FullScreenLoader />;
  }

  return (
    <View style={styles.root}>
      <Header title={propertyName} {...header} />
      {phoneNumber && (
        <View style={[{ backgroundColor: colors.white }, styles.phoneNumber]}>
          {showNewTourButton && (
            <TouchableHighlight
              style={styles.showNewTourButton}
              onPress={async () => {
                if (route && route.path === "/tour/:tourToken") {
                  await handleReset();
                } else {
                  setShowConfirmation(true);
                }
              }}
            >
              <Typography
                style={[
                  { color: colors.primary },
                  styles.showNewTourButtonText,
                ]}
              >
                {route &&
                [
                  "/tour/:tourToken/sms-verification-consent",
                  "/property/:groupTourConfigUuid/prospect-waitlist/sms-verification-consent",
                ].includes(route.path as string)
                  ? "Cancel"
                  : route && route.path === "/tour/:tourToken"
                    ? "New Tour"
                    : "Start Over"}
              </Typography>
            </TouchableHighlight>
          )}
          {phoneNumber && (
            <Typography
              style={[
                styles.displayformattedPhoneNumberText,
                {
                  textAlign: showNewTourButton ? "right" : "center",
                },
              ]}
            >
              {"Need Help? "}
              <Link type="bodySemibold" href={`tel:${phoneNumber}`}>
                <Typography
                  style={[
                    { color: colors.primary },
                    styles.displayformattedPhoneNumber,
                  ]}
                >
                  {formattedPhoneNumber}
                </Typography>
              </Link>
            </Typography>
          )}
        </View>
      )}
      <View style={styles.container}>{children}</View>
      {showConfirmation && (
        <View style={styles.showConfirmationText}>
          <View
            style={[
              styles.content,
              {
                ...shadows.regular,
              },
            ]}
          >
            <View>
              <Typography style={styles.startOverText}>Start Over?</Typography>
              <Typography style={styles.startOverDetails}>
                Starting over will clear your information and take you back to
                the first screen. Do you wish to continue?
              </Typography>
            </View>
            <VStack spacing={12}>
              <Button onPress={() => handleReset()} size="large">
                Continue
              </Button>
              <Button
                variation="plain"
                size="large"
                onPress={() => setShowConfirmation(false)}
              >
                Cancel
              </Button>
            </VStack>
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    width: "100%",
    height: "100%",
    position: "relative",
    overflow: "hidden",
    backgroundColor: "#ffffff",
    zIndex: 2,
  },
  container: {
    height: "calc(100% - 80px)",
    paddingBottom: 80,
  },
  phoneNumber: {
    shadowColor: "rgba(45, 50, 57, .2)",
    shadowRadius: 8,
    shadowOffset: {
      width: 0,
      height: 4,
    },
    flexDirection: "row",
    justifyContent: "space-between",
    padding: 16,
  },
  showNewTourButton: { flex: 1 },
  showNewTourButtonText: {
    fontWeight: "600",
  },
  displayformattedPhoneNumberText: {
    fontWeight: "600",
    flex: 3,
  },
  displayformattedPhoneNumber: {
    textDecorationLine: "underline",
    fontWeight: "600",
  },
  showConfirmationText: {
    flex: 1,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, .2)",
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 10,
    justifyContent: "center",
    alignItems: "center",
  },
  content: {
    backgroundColor: "#FFFFFF",
    margin: 24,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 24,
  },
  startOverText: {
    fontFamily: "Open Sans",
    fontSize: 21,
    fontWeight: "300",
    marginBottom: 16,
    textAlign: "center",
  },
  startOverDetails: {
    fontFamily: "Open Sans",
    fontSize: 16,
    marginBottom: 24,
    textAlign: "center",
  },
});
